import { TextArea as BlueprintTextArea } from '@blueprintjs/core';
import { Cognitive, SendFilled } from '@carbon/icons-react';
import * as React from 'react';

import { useVoiceboxConversation } from 'bones/components/molecules/Voicebox/context/VoiceboxConversation';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { useVoiceboxSettings } from 'bones/components/molecules/Voicebox/context/VoiceboxSettings';
import {
  VoiceboxApp,
  VoiceboxRequestMessage,
} from 'bones/constants/voicebox/message';
import { Button } from 'bones/design-system/components/Button';
import { Textarea } from 'bones/design-system/components/form/Textarea';
import { LoadingIcon } from 'bones/design-system/components/icons/LoadingIcon';
import { Link } from 'bones/design-system/components/Link';
import { usePortalDashboardUrl } from 'bones/utils/portal/usePortalDashboardUrl';
import { usePortalFeatures } from 'bones/utils/portal/usePortalFeatures';

export interface VoiceboxChatTextareaProps {
  aiReasoning: boolean;
  lastTypedQuery: string;
  query: string;
  queryHistoryIdx: number;
  sendMessage: (message: string) => void;
  sendMessageDisabled: boolean;
  setAiReasoning: (aiReasoning: boolean) => void;
  setLastTypedQuery: (query: string) => void;
  setQuery: (query: string) => void;
  setQueryHistoryIdx: (queryHistoryIdx: number) => void;
  setIsSuggestedPromptsOpen?: (isSuggestedPromptsShown: boolean) => void;
}

export const VoiceboxChatTextarea: React.VFC<VoiceboxChatTextareaProps> = ({
  aiReasoning,
  lastTypedQuery,
  query,
  queryHistoryIdx,
  sendMessage,
  sendMessageDisabled,
  setAiReasoning,
  setLastTypedQuery,
  setQuery,
  setQueryHistoryIdx,
  setIsSuggestedPromptsOpen,
}) => {
  const { voiceboxAiReasoning } = usePortalFeatures();
  const {
    config,
    copy,
    testIds,
    links: { openLinksInNewTab = true },
  } = useVoiceboxSchema();
  const { isRefetchingConversation, messages } = useVoiceboxConversation();
  const { catalogProvidersMissing } = useVoiceboxSettings();

  const dashboardUrl = usePortalDashboardUrl(config.connectionIdx);

  const requestQueries = React.useMemo(
    () =>
      messages
        .filter((message): message is VoiceboxRequestMessage =>
          Boolean(message.isRequest)
        )
        .map((message) => message.query)
        .concat([''])
        .reverse(),
    [messages]
  );
  const handleNavigateQueryHistory = (offset: number) => {
    const nextMessageIdx = Math.min(
      Math.max(0, queryHistoryIdx + offset),
      requestQueries.length - 1
    );
    setQueryHistoryIdx(nextMessageIdx);
    setQuery(nextMessageIdx ? requestQueries[nextMessageIdx] : lastTypedQuery);
  };

  const handleSendMessage = (message: string) => {
    if (sendMessageDisabled) {
      return;
    }

    const trimmed = message.trim();
    if (!trimmed) {
      return;
    }
    sendMessage(trimmed);
  };

  const textAreaRef = React.useRef<BlueprintTextArea>(null);
  React.useEffect(() => {
    if (!query && textAreaRef.current?.textareaElement) {
      // reset text area height when query is cleared
      textAreaRef.current.setState({ height: undefined });
    }
  }, [query, textAreaRef]);

  return (
    <div className="vb-voicebox-chat-textarea">
      <Textarea
        actions={
          <>
            <div>
              {config.app !== VoiceboxApp.DESIGNER && voiceboxAiReasoning ? (
                <Button
                  active={aiReasoning}
                  className="vb-voicebox-chat-reasoning-button"
                  leftIcon={Cognitive}
                  onClick={() => {
                    setAiReasoning(!aiReasoning);
                  }}
                  size="xs"
                  testId={testIds.drawer.aiReasoning}
                  variant="tertiary-gray"
                >
                  {copy.drawer.aiReasoning}
                </Button>
              ) : null}
            </div>
            <div>
              <Button
                className="vb-voicebox-chat-textarea-button"
                disabled={sendMessageDisabled}
                leftIcon={isRefetchingConversation ? LoadingIcon : SendFilled}
                leftIconProps={{
                  testId: isRefetchingConversation
                    ? testIds.drawer.loading.conversationRefetch
                    : undefined,
                }}
                onClick={() => {
                  handleSendMessage(query);
                  setIsSuggestedPromptsOpen?.(false);
                }}
                size="lg"
                testId={testIds.drawer.submit}
                variant="primary-accent"
              />
            </div>
          </>
        }
        autoComplete="off"
        autoFocus
        growVertically
        helpProps={
          config.app !== VoiceboxApp.DESIGNER && catalogProvidersMissing
            ? {
                text: (
                  <>
                    {copy.message.noCatalogProviders.beforeLink}
                    &nbsp;
                    <Link
                      href={`${dashboardUrl}knowledge-catalog/#/`}
                      rel="noreferrer"
                      size="md"
                      target={openLinksInNewTab ? '_blank' : '_self'}
                      testId={testIds.messages.noCatalogProviders.link}
                    >
                      {copy.message.noCatalogProviders.link}
                    </Link>
                    &nbsp;
                    {copy.message.noCatalogProviders.afterLink}
                  </>
                ),
                testId: testIds.messages.noCatalogProviders.message,
              }
            : undefined
        }
        label=""
        onChange={(evt) => {
          setLastTypedQuery(evt.target.value);
          setQueryHistoryIdx(0);
          setQuery(evt.target.value);
        }}
        onKeyDown={(evt) => {
          if (
            evt.key === 'ArrowUp' &&
            !evt.altKey &&
            !evt.ctrlKey &&
            !evt.shiftKey &&
            !evt.metaKey &&
            evt.currentTarget.selectionStart === 0 &&
            evt.currentTarget.selectionEnd === 0
          ) {
            evt.preventDefault();
            handleNavigateQueryHistory(+1);
          } else if (
            evt.key === 'ArrowDown' &&
            !evt.altKey &&
            !evt.ctrlKey &&
            !evt.shiftKey &&
            !evt.metaKey &&
            evt.currentTarget.selectionStart ===
              evt.currentTarget.value.length &&
            evt.currentTarget.selectionEnd === evt.currentTarget.value.length
          ) {
            evt.preventDefault();
            handleNavigateQueryHistory(-1);
          } else if (evt.key === 'Enter' && !evt.altKey && !evt.shiftKey) {
            evt.preventDefault();
            handleSendMessage(query);
            setIsSuggestedPromptsOpen?.(false);
          }
        }}
        placeholder={copy.drawer.placeholder}
        testId={testIds.drawer.input}
        textAreaRef={textAreaRef}
        value={query}
      />
    </div>
  );
};
